import { html, nothing, PropertyValueMap, unsafeCSS } from 'lit'
import { property, query, state } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'

import type { ButtonVariant } from '@/common/types'
import { DialogState } from '@/dap-design-system'
import { DdsElement } from '@/internal/dds-hu-element'
import { t } from '@/localization/localization'

import DapDSButton from '../button/button.component'
import DapDSIcon from '../icon/icon.component'
import styles from './image-zoom.scss?inline'

/**
 * `dap-ds-image-zoom`
 * @summary An image zoom component that provides a Medium.com-style zoom experience. Click an image to expand it to fill the viewport with a smooth animation.
 * @element dap-ds-image-zoom
 * @title - Image Zoom
 *
 * @property {boolean} open - The open/zoomed state. Can be used for controlled mode.
 * @property {boolean} isDisabled - Disables zoom functionality.
 * @property {number} zoomMargin - Margin in pixels from viewport edges when zoomed. Default is 0.
 * @property {string} expandButtonAriaLabel - Accessible label for the zoom trigger. Default is 'Expand image'.
 * @property {string} unzoomButtonAriaLabel - Accessible label for the unzoom button. Default is 'Minimize image'.
 * @property {boolean} hideButtons - Hides the expand and unzoom buttons. Default is false.
 * @property {boolean} canSwipeToUnzoom - Enables swipe gesture to close when zoomed. Default is true.
 * @property {number} swipeToUnzoomThreshold - Swipe distance in pixels required to trigger unzoom. Default is 10.
 * @property {string} zoomSrc - URL of a higher quality image to display when zoomed. Falls back to the slotted image src.
 *
 * @event {CustomEvent} dds-zoom - Fires when the image is about to zoom. Cancelable via event.preventDefault().
 * @event {CustomEvent} dds-unzoom - Fires after the image has unzoomed.
 *
 * @slot - The image or content element to zoom. Should contain an <img> element.
 *
 * @csspart trigger - The zoom trigger wrapper element.
 * @csspart dialog - The zoom dialog element.
 * @csspart overlay - The backdrop overlay element.
 * @csspart zoomed-image - The zoomed image element.
 * @csspart unzoom-button - The button to close the zoomed view.
 * @csspart expand-button - The button to expand the image.
 * @csspart expand-button-base - The base of the expand button.
 * @csspart expand-button-content - The content of the expand button.
 * @csspart unzoom-button-base - The base of the unzoom button.
 * @csspart unzoom-button-content - The content of the unzoom button.
 * @csspart expand-button-icon - The icon of the expand button.
 * @csspart unzoom-button-icon - The icon of the unzoom button.
 *
 * @cssproperty --dds-image-zoom-overlay-bg - Overlay background color (default: rgba(0, 0, 0, 0.8)).
 * @cssproperty --dds-image-zoom-transition-speed - Animation duration (default: 300ms).
 * @cssproperty --dds-image-zoom-transition-timing - Animation timing function (default: var(--dds-easing-ease-in-out, ease-in-out)).
 */
export default class DapDSImageZoom extends DdsElement {
  static tagName = 'dap-ds-image-zoom'

  static dependencies = {
    'dap-ds-button': DapDSButton,
    'dap-ds-icon': DapDSIcon,
  }

  static readonly styles = unsafeCSS(styles)

  @query('.image-zoom__dialog') private _dialog!: HTMLDialogElement

  /** The open/zoomed state. */
  @property({ type: Boolean, reflect: true }) open = false

  /** Disables zoom functionality. */
  @property({ type: Boolean }) isDisabled = false

  /** Margin in pixels from viewport edges when zoomed. */
  @property({ type: Number }) zoomMargin = 0

  /** Accessible label for the zoom trigger. */
  @property() expandButtonAriaLabel: string | null = null

  /** Accessible label for the unzoom button. */
  @property() unzoomButtonAriaLabel: string | null = null

  /** Enable swipe down gesture to close when zoomed. */
  @property({ type: Boolean }) canSwipeToUnzoom = true

  /** Distance in pixels a swipe must travel to trigger unzoom. */
  @property({ type: Number }) swipeToUnzoomThreshold = 10

  /** URL of a higher quality image to display when zoomed. Falls back to slotted image src. */
  @property() zoomSrc?: string

  /** Hides the expand and unzoom buttons. Useful when the host UI provides its own controls. */
  @property({ type: Boolean }) hideButtons = false

  @property() zoomButtonVariant: ButtonVariant = 'primary'

  /** `data-testid` for the expand/zoom button. */
  @property() expandButtonTestId = 'image-zoom-expand-button'

  /** `data-testid` for the unzoom/close button. */
  @property() unzoomButtonTestId = 'image-zoom-unzoom-button'

  // Managed imperatively to avoid update-in-update Lit warnings
  private _dialogState: DialogState = 'closed'

  // Updated via slotchange — not @state to avoid re-render chains
  @state() private _slottedImgSrc = ''
  @state() private _slottedImgAlt = ''

  private _slottedImg: HTMLImageElement | null = null

  private _touchStartY = 0
  private _closeTimeout?: number
  private _previouslyFocusedElement: Element | null = null

  disconnectedCallback(): void {
    super.disconnectedCallback()
    clearTimeout(this._closeTimeout)
    if (this._dialog?.open) {
      this._dialog.close()
    }
    document.body.style.overflow = ''
  }

  protected updated(
    changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.updated(changedProperties)

    if (changedProperties.has('open')) {
      const prev = changedProperties.get('open') as boolean | undefined
      if (this.open && !prev) {
        this._openDialog()
      } else if (
        !this.open &&
        prev &&
        this._dialogState !== 'closing' &&
        this._dialogState !== 'closed'
      ) {
        this._animateClose()
      }
    }
  }

  // Updates dialog's data-state attribute imperatively (no Lit re-render)
  private _setDialogState(state: DialogState): void {
    this._dialogState = state
    if (this._dialog) {
      this._dialog.dataset['state'] = state
    }
  }

  private _handleSlotChange(): void {
    const slot =
      this.shadowRoot?.querySelector<HTMLSlotElement>('slot:not([name])')
    const nodes = slot?.assignedElements({ flatten: true }) ?? []

    let img: HTMLImageElement | null = null
    for (const el of nodes) {
      if (el.tagName === 'IMG') {
        img = el as HTMLImageElement
        break
      }
      const found = el.querySelector<HTMLImageElement>('img')
      if (found) {
        img = found
        break
      }
    }

    this._slottedImg = img
    this._slottedImgSrc =
      img?.currentSrc || img?.src || img?.getAttribute('src') || ''
    this._slottedImgAlt = img?.alt || img?.getAttribute('alt') || ''
  }

  private _openDialog(): void {
    this._previouslyFocusedElement = document.activeElement

    // Re-read src at open time — currentSrc may have been empty at slotchange
    if (this._slottedImg && !this.zoomSrc) {
      const img = this._slottedImg
      this._slottedImgSrc =
        img.currentSrc || img.src || img.getAttribute('src') || ''
    }

    const event = this.emit('dds-zoom', undefined, { cancelable: true })
    if (event.defaultPrevented) {
      this.open = false
      return
    }

    // Set state imperatively — no reactive update triggered
    this._setDialogState('opening')
    this._dialog.showModal()
    document.body.style.overflow = 'hidden'

    requestAnimationFrame(() => {
      this._setDialogState('open')
      this._dialog
        .querySelector<HTMLButtonElement>('.image-zoom__unzoom-btn')
        ?.focus()
    })
  }

  private _animateClose(): void {
    if (this._dialogState === 'closing' || this._dialogState === 'closed')
      return

    this._setDialogState('closing')

    let settled = false
    const onDone = () => {
      if (settled) return
      settled = true
      clearTimeout(this._closeTimeout)
      this._dialog?.close()
      this._setDialogState('closed')
      document.body.style.overflow = ''

      if (this._previouslyFocusedElement instanceof HTMLElement) {
        this._previouslyFocusedElement.focus()
      }

      this.emit('dds-unzoom')
    }

    const zoomed = this._dialog?.querySelector<HTMLElement>(
      '.image-zoom__zoomed',
    )
    if (zoomed) {
      zoomed.addEventListener('transitionend', onDone, { once: true })
      this._closeTimeout = window.setTimeout(onDone, 500)
    } else {
      onDone()
    }
  }

  /** Programmatically zoom the image. */
  zoom(): void {
    if (this.isDisabled || this.open) return
    this.open = true
  }

  /** Programmatically unzoom the image. */
  unzoom(): void {
    if (!this.open) return
    this._animateClose()
    this.open = false
  }

  private _handleTriggerClick(): void {
    this.zoom()
  }

  private _handleTriggerKeydown(e: KeyboardEvent): void {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      this.zoom()
    }
  }

  private _handleDialogCancel(e: Event): void {
    e.preventDefault()
    this.unzoom()
  }

  private _handleTouchStart(e: TouchEvent): void {
    if (!this.canSwipeToUnzoom) return
    this._touchStartY = e.touches[0].clientY
  }

  private _handleTouchEnd(e: TouchEvent): void {
    if (!this.canSwipeToUnzoom) return
    const delta = Math.abs(e.changedTouches[0].clientY - this._touchStartY)
    if (delta > this.swipeToUnzoomThreshold) {
      this.unzoom()
    }
  }

  render() {
    const resolvedSrc = this.zoomSrc ?? this._slottedImgSrc
    const resolvedAlt = this._slottedImgAlt

    return html`
      <div
        part="trigger"
        class="image-zoom__trigger"
        role="button"
        tabindex=${ifDefined(this.isDisabled ? undefined : 0)}
        aria-label=${[
          this._slottedImgAlt,
          this.expandButtonAriaLabel ?? t('image-zoom.expand'),
        ]
          .filter(Boolean)
          .join(', ')}
        aria-expanded=${this.open ? 'true' : 'false'}
        aria-disabled=${ifDefined(this.isDisabled ? 'true' : undefined)}
        @click=${this._handleTriggerClick}
        @keydown=${this._handleTriggerKeydown}>
        <slot @slotchange=${this._handleSlotChange}></slot>
        ${!this.isDisabled && !this.hideButtons
          ? html`
              <dap-ds-button
                variant=${this.zoomButtonVariant}
                shape="circle"
                part="expand-button"
                data-testid=${this.expandButtonTestId}
                class="image-zoom__expand-button"
                aria-hidden="true"
                exportparts="base:expand-button-base, content:expand-button-content"
                tabindex="-1">
                <dap-ds-icon
                  aria-hidden="true"
                  name="zoom-in-line"
                  part="expand-button-icon">
                </dap-ds-icon>
              </dap-ds-button>
            `
          : nothing}
      </div>

      <dialog
        class="image-zoom__dialog"
        part="dialog"
        data-state=${this._dialogState}
        @cancel=${this._handleDialogCancel}
        @touchstart=${this._handleTouchStart}
        @touchend=${this._handleTouchEnd}>
        <div
          class="image-zoom__overlay"
          part="overlay"
          @click=${() => this.unzoom()}></div>

        <div
          class="image-zoom__content-area"
          style="--dds-image-zoom-margin: ${this.zoomMargin}px">
          ${resolvedSrc
            ? html`
                <img
                  class="image-zoom__zoomed"
                  part="zoomed-image"
                  src=${resolvedSrc}
                  alt=${resolvedAlt}
                  @click=${() => this.unzoom()} />
              `
            : nothing}
        </div>

        ${!this.hideButtons
          ? html`
              <dap-ds-button
                class="image-zoom__unzoom-btn"
                part="unzoom-button"
                data-testid=${this.unzoomButtonTestId}
                variant=${this.zoomButtonVariant}
                shape="circle"
                aria-label=${this.unzoomButtonAriaLabel ??
                t('image-zoom.unzoom')}
                exportparts="base:unzoom-button-base, content:unzoom-button-content"
                @click=${() => this.unzoom()}>
                <dap-ds-icon
                  aria-hidden="true"
                  name="zoom-out-line"
                  part="unzoom-button-icon">
                </dap-ds-icon>
              </dap-ds-button>
            `
          : nothing}
      </dialog>
    `
  }
}
